// src/components/Select.ts
import { LitElement, html, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { BehaviorSubject } from 'rxjs'
import { ComponentVariant } from '../types'

export interface Option {
    label: string
    value: string
}

@customElement('e-select')
export class ESelect extends LitElement {
    private _value$ = new BehaviorSubject('')

    @property({ type: String }) variant: ComponentVariant = 'accent'
    @property({ type: String }) size: 'small' | 'medium' | 'large' = 'medium'
    @property({ type: Boolean }) fullWidth = false
    @property({ type: Boolean }) grow = false
    @property({ type: Array }) options: Option[] = []

    @property({ type: String })
    get value() {
        return this._value$.value
    }

    set value(v) {
        this._value$.next(v)
    }

    static styles = css`
        :host {
            display: inline-flex;
            box-sizing: border-box;
        }

        :host([fullWidth]) {
            width: 100%;
        }

        :host([grow]) {
            flex-grow: 1;
            width: auto;
        }

        select {
            font-family: var(--base-font-family, sans-serif);
            font-size: var(--font-size-button);
            color: var(--text-primary);
            background-color: var(--bg-secondary);
            border: 2px solid var(--border-color);
            padding: var(--s0) var(--s1);
            border-radius: var(--radius-medium);
            transition: var(--transition-fast);
            box-sizing: border-box;
            flex: 1;
            min-width: 0;
            height: var(--control-height);
            cursor: pointer;
        }

        /* Variant styles */
        select.variant-accent {
            border-color: var(--primary-color);
        }

        select.variant-playful {
            border-color: var(--interactive-color);
        }

        select.variant-gradient {
            border: 2px solid;
            border-image-slice: 1;
            border-image-source: linear-gradient(
                to right,
                var(--gradient-start),
                var(--gradient-end)
            );
        }

        select:focus {
            outline: none;
            box-shadow: 0 0 0 2px var(--primary-color);
        }
    `

    #handleChange(e: { target: { value: string } }) {
        this.value = e.target.value
        this.dispatchEvent(
            new CustomEvent('value-changed', {
                detail: { value: this.value },
                bubbles: true,
                composed: true,
            }),
        )
    }

    render() {
        const selectClassList = [`size-${this.size}`, `variant-${this.variant}`].join(' ')

        return html`
            <select class="${selectClassList}" @change="${this.#handleChange}">
                ${this.options.map(
                    option => html`
                        <option value="${option.value}" ?selected="${this.value === option.value}">
                            ${option.label}
                        </option>
                    `,
                )}
            </select>
        `
    }
}
